// /app/frontend/src/pages/CloverLaunchPage.jsx
//
// Clover App Market "Alternate Launch Path" entry point.
//
// When Clover opens `/dashboard/integrations/clover/launch`, we:
//   1. Rely on <Protected> (route-level) to enforce login.
//   2. Immediately POST /api/company/pos-config/clover/connect
//      — the same endpoint the Commerce Settings "Connect Clover"
//      button calls (see CommerceSettings.jsx L426-443).
//   3. Redirect the browser to the returned `authorize_url`.
//
// No UI redesign. No new backend route. No changes to OAuth,
// callback URI, token storage, refresh logic, or tenant isolation.
// The existing callback (/api/company/pos-config/clover/oauth/callback)
// finishes the flow and lands the user back on Commerce Settings.
//
// The Commerce Settings page remains fully functional for manual
// management of the Clover connection — this launch page exists
// solely so Clover's App Market entry point can start OAuth in one
// hop without asking the merchant to click "Connect Clover" again.
import React from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

export default function CloverLaunchPage() {
  const [errorMsg, setErrorMsg] = React.useState(null);
  const started = React.useRef(false);

  React.useEffect(() => {
    // React StrictMode / rerenders may double-invoke effects in dev; the
    // authorize_url handshake is idempotent server-side (state row is
    // single-use), but avoiding a second POST keeps things clean.
    if (started.current) return;
    started.current = true;

    (async () => {
      try {
        const { data } = await api.post(
          "/company/pos-config/clover/connect",
          {},
        );
        if (data && typeof data.authorize_url === "string" && data.authorize_url) {
          window.location.href = data.authorize_url;
          return;
        }
        setErrorMsg("Clover did not return an authorization URL.");
      } catch (e) {
        const det = e?.response?.data?.detail;
        const msg =
          typeof det === "string"
            ? det
            : det?.message || det?.code || "Failed to start Clover authorization.";
        setErrorMsg(msg);
      }
    })();
  }, []);

  return (
    <div
      className="min-h-screen grid place-items-center px-6"
      data-testid="clover-launch-page"
    >
      <div className="max-w-md w-full text-center space-y-4">
        {!errorMsg ? (
          <>
            <div
              className="text-sm text-muted-foreground"
              data-testid="clover-launch-status"
            >
              Redirecting to Clover to authorize your account…
            </div>
          </>
        ) : (
          <>
            <div
              className="text-base font-medium text-red-600"
              data-testid="clover-launch-error"
            >
              {errorMsg}
            </div>
            <div className="text-sm text-muted-foreground">
              You can also connect Clover manually from Commerce Settings.
            </div>
            <div>
              <Link
                to="/dashboard/commerce-settings?tab=payments"
                className="text-sm underline"
                data-testid="clover-launch-manual-link"
              >
                Go to Commerce Settings
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
